/* ==========================================================================
   DIREVULF — plp.js
   Collection and search page behaviour: filters, sort, and the size-aware
   parts of the product card.

   Three rules this file follows, because they are what make the size-aware
   card honest rather than theatrical:

     1. The size preference is the SHOPPER'S OWN and never leaves their
        device. It is a single size label in localStorage — no measurements,
        no profile, nothing that identifies anybody.
     2. Everything the stock strip says is computed from the variant JSON
        Shopify rendered into the card. If a product does not offer the saved
        size, the strip stays hidden rather than saying something vague.
     3. A remaining-units number appears only when the merchant has switched
        Low stock display on AND Shopify actually reported a tracked quantity
        for that exact variant.

   Progressive enhancement throughout: every form here is a real GET/POST form
   that works with this file absent.
   ========================================================================== */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* --------------------------------------------------------------- config */
  var CFG = { lowStock: false, threshold: 5, sizeAware: true, strings: {} };
  (function readConfig() {
    var node = $('[data-dv-shop-config]');
    if (!node) return;
    try { CFG = Object.assign(CFG, JSON.parse(node.textContent)); } catch (e) { /* keep defaults */ }
  })();

  /* Placeholders use the %TOKEN% convention already used elsewhere in this
     theme, so a locale string never has to survive Liquid's own
     interpolation on its way into JavaScript. */
  function t(key, vars) {
    var s = CFG.strings && CFG.strings[key];
    if (typeof s !== 'string') return '';
    if (!vars) return s;
    Object.keys(vars).forEach(function (k) {
      s = s.split('%' + k.toUpperCase() + '%').join(vars[k]);
    });
    return s;
  }

  /* ------------------------------------------------- size preference store */
  var SIZE_KEY = 'dv:size';
  function readSize() {
    try { return window.localStorage.getItem(SIZE_KEY) || ''; } catch (e) { return ''; }
  }
  function writeSize(value) {
    if (!value) return;
    try { window.localStorage.setItem(SIZE_KEY, String(value)); } catch (e) { /* private mode */ }
    document.dispatchEvent(new CustomEvent('dv:size:change', { detail: { size: String(value) } }));
  }
  function clearSize() {
    try { window.localStorage.removeItem(SIZE_KEY); } catch (e) { /* ignore */ }
    document.dispatchEvent(new CustomEvent('dv:size:change', { detail: { size: '' } }));
  }
  window.DirevulfSize = { get: readSize, set: writeSize, clear: clearSize };

  /* ---------------------------------------------------------------- panes */
  /* The filter sheet's left rail. Plain tabs: one selected, the rest hidden. */
  function initPanes() {
    document.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-dv-pane-tab]');
      if (!tab) return;
      var scope = tab.closest('form') || document;
      var id = tab.getAttribute('data-dv-pane-tab');
      $$('[data-dv-pane-tab]', scope).forEach(function (other) {
        other.setAttribute('aria-selected', String(other === tab));
      });
      $$('[data-dv-pane]', scope).forEach(function (pane) {
        pane.hidden = pane.id !== id;
      });
    });
  }

  /* ------------------------------------------------ results, in place */
  /* FILTERING AND SORTING UPDATE THE PAGE; THEY DO NOT RELOAD IT.

     Every filter tick and every sort used to call form.submit() or
     location.assign(): a full page load each time. The page went blank, was
     rebuilt from the top (header, loader check, fonts, every script), the
     sidebar came back with its groups collapsed and its scroll position lost,
     and the #dv-products anchor then jumped the window down to the grid. That
     is the flash-and-jump in the recording.

     Now the same URL the form would have produced is asked for through
     Shopify's Section Rendering API (?section_id=<this section>), which
     returns just this section rendered by the same Liquid. Only the parts that
     depend on the filters are swapped — the heading (count and sort), the
     sidebar, the results, the pager, the phone's bottom bar and the two
     sheets — each in one step, so there is never an empty frame:

     - the current cards stay on screen until the new ones are ready, and the
       first row's images are fetched and decoded BEFORE the swap, so the new
       grid does not paint as blank tiles;
     - the sidebar keeps its scroll position, which groups are open, which
       "+ more" lists are expanded, and the focused checkbox;
     - a request that is overtaken by a newer choice is aborted, so only the
       latest answer is ever drawn;
     - the address bar gets the real filtered URL (pushState), so it can be
       shared and reloaded, and Back / Forward re-render the page they point
       at (popstate). Pagination links stay ordinary links.

     If anything fails — no fetch, a network error, a non-200 — the browser is
     simply sent to that URL, which is exactly the old behaviour. Without
     JavaScript the forms submit normally.

     Every listener is delegated from the document and bound ONCE, so
     replacing the sidebar or the sheets never needs re-binding and can never
     stack a second handler on the same control. */
  var REGIONS = [
    '[data-dv-region="head"]',
    '[data-dv-region="sidebar"]',
    '[data-dv-collection-body]',
    '[data-dv-region="pager"]',
    '[data-dv-region="mbar"]',
    '[data-dv-region="filters-sheet"]',
    '[data-dv-region="sort-sheet"]'
  ];
  var current = window.location.pathname + window.location.search;
  var inflight = null;
  var seq = 0;

  function sectionRoot() { return $('[data-dv-section-id]'); }

  function cleanUrl(href) {
    var u = new URL(href, window.location.href);
    u.searchParams.delete('section_id');
    u.hash = '';
    return u;
  }

  function urlFromForm(form) {
    var action = new URL(form.getAttribute('action') || window.location.pathname, window.location.href);
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (typeof value !== 'string' || value === '') return;
      params.append(key, value);
    });
    var q = params.toString();
    return action.pathname + (q ? '?' + q : '');
  }

  /* What must survive a swap of one region. */
  function keep(region) {
    var state = { scroll: region.scrollTop, open: {}, expanded: {}, focus: null };
    $$('details[data-dv-facet-key]', region).forEach(function (d) { state.open[d.getAttribute('data-dv-facet-key')] = d.open; });
    $$('[data-dv-facet-values].is-expanded', region).forEach(function (l) { state.expanded[l.getAttribute('data-dv-facet-values')] = true; });
    var a = document.activeElement;
    if (a && a !== document.body && region.contains(a)) {
      if (a.name && a.type === 'checkbox') state.focus = 'input[name="' + CSS.escape(a.name) + '"][value="' + CSS.escape(a.value) + '"]';
      else if (a.id) state.focus = '#' + CSS.escape(a.id);
      else if (a.getAttribute('data-dv-open')) state.focus = '[data-dv-open="' + CSS.escape(a.getAttribute('data-dv-open')) + '"]';
    }
    return state;
  }

  function restore(region, state) {
    $$('details[data-dv-facet-key]', region).forEach(function (d) {
      var k = d.getAttribute('data-dv-facet-key');
      /* A group the shopper had open stays open. A group the server opens
         (one with an active value) is never closed again by the swap. */
      if (state.open[k] === true) d.open = true;
    });
    Object.keys(state.expanded).forEach(function (gid) {
      var list = $('[data-dv-facet-values="' + CSS.escape(gid) + '"]', region);
      var more = $('[data-dv-facet-more="' + CSS.escape(gid) + '"]', region);
      if (!list || list.classList.contains('is-expanded')) return;
      list.classList.add('is-expanded');
      $$('[data-overflow]', list).forEach(function (row) { row.hidden = false; });
      if (more) more.textContent = more.getAttribute('data-label-less') || more.textContent;
    });
    region.scrollTop = state.scroll;
    if (state.focus) {
      var f = $(state.focus, region);
      if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
    }
  }

  /* Fetch and decode the first row of new images before anything is swapped,
     so the new cards paint with their photos. Capped: a slow image is not
     allowed to hold the results back. */
  function preload(doc) {
    var imgs = $$('[data-dv-collection-body] img', doc).slice(0, 8);
    if (!imgs.length) return Promise.resolve();
    var loads = imgs.map(function (src) {
      return new Promise(function (resolve) {
        var img = new Image();
        if (src.getAttribute('sizes')) img.sizes = src.getAttribute('sizes');
        if (src.getAttribute('srcset')) img.srcset = src.getAttribute('srcset');
        img.src = src.getAttribute('src') || '';
        if (img.decode) img.decode().then(resolve, resolve);
        else { img.onload = img.onerror = resolve; }
      });
    });
    return Promise.race([Promise.all(loads), new Promise(function (r) { window.setTimeout(r, 900); })]);
  }

  function render(doc) {
    REGIONS.forEach(function (sel) {
      var cur = $(sel);
      var next = $(sel, doc);
      if (!cur || !next) return;
      var state = keep(cur);
      cur.innerHTML = next.innerHTML;
      restore(cur, state);
    });
    var title = $('title', doc);
    if (title && title.textContent) document.title = title.textContent;
  }

  function settle() {
    document.dispatchEvent(new CustomEvent('dv:section:loaded'));
    if (window.DirevulfWishlist && window.DirevulfWishlist.render) window.DirevulfWishlist.render();
    if (window.DirevulfSortbox && window.DirevulfSortbox.mount) window.DirevulfSortbox.mount(document);
    /* Say what changed, once, for screen readers. */
    var count = $('[data-dv-region="head"] .item-count, [data-dv-region="head"] .dv-search__count');
    var region = document.getElementById('dv-live-region');
    if (region && count) { region.textContent = ''; window.setTimeout(function () { region.textContent = count.textContent.replace(/^[\s—-]+/, ''); }, 60); }
    /* Only when the top of the results has scrolled out of view: bring it
       back, so a change made from deep in the grid is seen. Near the top the
       page does not move at all. */
    var head = document.getElementById('dv-products');
    if (head) {
      var top = head.getBoundingClientRect().top;
      if (top < 0) {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(window.pageYOffset + top - 16, 0), behavior: reduce ? 'auto' : 'smooth' });
      }
    }
  }

  function go(href, opts) {
    opts = opts || {};
    var url = cleanUrl(href);
    var target = url.pathname + url.search;
    var root = sectionRoot();
    if (!root || !window.fetch || !window.DOMParser || url.origin !== window.location.origin) {
      window.location.assign(target);
      return;
    }
    if (!opts.fromPop && target === current) return;

    var mine = ++seq;
    if (inflight) { try { inflight.abort(); } catch (e) { /* ignore */ } }
    inflight = window.AbortController ? new AbortController() : null;

    var body = $('[data-dv-collection-body]');
    if (body) body.setAttribute('aria-busy', 'true');
    /* Dim only if the answer is slow; a quick one never flashes anything. */
    var dim = window.setTimeout(function () { if (body && mine === seq) body.classList.add('is-updating'); }, 220);

    var fetchUrl = new URL(url.href);
    fetchUrl.searchParams.set('section_id', root.getAttribute('data-dv-section-id'));

    window.fetch(fetchUrl.href, { credentials: 'same-origin', signal: inflight ? inflight.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (mine !== seq) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!$('[data-dv-collection-body]', doc)) throw new Error('no results in response');
        return preload(doc).then(function () {
          if (mine !== seq) return;
          render(doc);
          current = target;
          if (!opts.fromPop) window.history.pushState({ dvPlp: true }, '', target);
          settle();
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (mine === seq) window.location.assign(target);
      })
      .then(function () {
        if (mine !== seq) return;
        window.clearTimeout(dim);
        var b = $('[data-dv-collection-body]');
        if (b) { b.classList.remove('is-updating'); b.setAttribute('aria-busy', 'false'); }
        inflight = null;
      });
  }

  window.DirevulfPLP = { go: go };

  function closeHostDialog(el) {
    var dialog = el && el.closest && el.closest('dialog');
    if (!dialog || !dialog.open) return;
    if (window.DirevulfOverlay && window.DirevulfOverlay.active === dialog) window.DirevulfOverlay.close();
    else dialog.close();
  }

  function initFacets() {
    if (!sectionRoot()) {
      /* A page with filters but no section marker: the old behaviour. */
      $$('[data-dv-facets-form][data-autoapply="true"]').forEach(function (form) {
        form.addEventListener('change', function (event) {
          if (!event.target.matches('input, select') || event.target.type === 'number' || event.target.type === 'search') return;
          form.submit();
        });
      });
      return;
    }

    /* The sidebar applies as soon as a value changes. */
    var timer;
    document.addEventListener('change', function (event) {
      var t = event.target;
      var form = t.closest && t.closest('[data-dv-facets-form][data-autoapply="true"]');
      if (!form || !t.matches('input, select')) return;
      if (t.type === 'number' || t.type === 'search') return;
      go(urlFromForm(form));
    });
    /* Price inputs wait for a pause, so typing 1499 is one request, not four. */
    document.addEventListener('input', function (event) {
      var t = event.target;
      var form = t.closest && t.closest('[data-dv-facets-form][data-autoapply="true"]');
      if (!form || t.type !== 'number') return;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { go(urlFromForm(form)); }, 700);
    });
    /* The phone sheet applies on its own button; Enter in a price field on
       desktop lands here too. */
    document.addEventListener('submit', function (event) {
      var form = event.target.closest && event.target.closest('[data-dv-facets-form], [data-dv-sort-sheet]');
      if (!form) return;
      event.preventDefault();
      closeHostDialog(form);
      go(urlFromForm(form));
    });
    /* Clear all, a removable pill, a sort option in the phone sheet: links to
       another view of this same page. */
    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var a = event.target.closest && event.target.closest(
        '[data-dv-facets-form] a[href], .applied-filters a[href], .dv-facets__active a[href], [data-dv-region="sort-sheet"] a[href]'
      );
      if (!a) return;
      var url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      event.preventDefault();
      closeHostDialog(a);
      go(url.href);
    });
    /* Back and Forward. */
    window.addEventListener('popstate', function () {
      var here = window.location.pathname + window.location.search;
      if (here !== current) go(window.location.href, { fromPop: true });
    });

    /* In-group search filters the rows already on the page. It never asks the
       server for a filter that is not there and never invents one. */
    document.addEventListener('input', function (event) {
      var input = event.target;
      if (!input.matches || !input.matches('[data-dv-facet-search]')) return;
      var form = input.closest('form') || document;
      var list = $('[data-dv-facet-values="' + CSS.escape(input.getAttribute('data-dv-facet-search')) + '"]', form);
      if (!list) return;
      var q = input.value.trim().toLowerCase();
      $$('[data-dv-facet-row]', list).forEach(function (row) {
        var label = row.getAttribute('data-label') || '';
        if (!q) row.hidden = row.hasAttribute('data-overflow') && !list.classList.contains('is-expanded');
        else row.hidden = label.indexOf(q) === -1;
      });
    });

    /* "+ N more" reveals the values already rendered below the fold. */
    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-dv-facet-more]');
      if (!button) return;
      var form = button.closest('form') || document;
      var list = $('[data-dv-facet-values="' + CSS.escape(button.getAttribute('data-dv-facet-more')) + '"]', form);
      if (!list) return;
      var expanded = list.classList.toggle('is-expanded');
      $$('[data-overflow]', list).forEach(function (row) { row.hidden = !expanded; });
      button.textContent = expanded
        ? (button.getAttribute('data-label-less') || button.textContent)
        : (button.getAttribute('data-label-more') || button.textContent);
    });

    hideAutoSubmit();
  }

  /* The sidebar has no Apply button once it applies itself. Re-run after
     every swap, because the swap brings the button back from Liquid. */
  function hideAutoSubmit() {
    $$('[data-dv-facets-form][data-autoapply="true"] [data-dv-facets-submit]').forEach(function (b) { b.hidden = true; });
  }
  document.addEventListener('dv:section:loaded', hideAutoSubmit);

  /* ------------------------------------------------------------ sort sheet */
  function initSortSheet() {
    var form = $('[data-dv-sort-sheet]');
    if (!form) return;
    var apply = $('[data-dv-sort-submit]', form);
    form.addEventListener('change', function (event) {
      if (event.target.name !== 'sort_by') return;
      closeHostDialog(form);
      go(urlFromForm(form));
    });
    if (apply) apply.hidden = true;
  }

  /* ----------------------------------------------------------- size chips */
  /* The "my size" chip on the toolbar is a shortcut into the same preference
     the PDP writes. It shows the saved size, or nothing at all. */
  function initSizeChip() {
    var chips = $$('[data-dv-size-chip]');
    if (!chips.length) return;
    function paint() {
      var size = readSize();
      chips.forEach(function (chip) {
        var out = $('[data-dv-size-chip-value]', chip);
        if (out) out.textContent = size;
        chip.hidden = !size;
      });
    }
    chips.forEach(function (chip) {
      var clear = $('[data-dv-size-chip-clear]', chip);
      if (clear) clear.addEventListener('click', function () { clearSize(); });
    });
    document.addEventListener('dv:size:change', paint);
    paint();
  }

  /* --------------------------------------------------------- account card */
  /* The account page's "Your size" card. The value lives on this device and
     nowhere else, so the server renders the card empty and this fills it in —
     or leaves the "you have not saved a size" line standing. */
  function initAccountSize() {
    var card = $('[data-dv-account-size]');
    if (!card) return;
    var value = $('[data-dv-account-size-value]', card);
    var none = $('[data-dv-account-size-none]', card);
    var clear = $('[data-dv-account-size-clear]', card);

    function paint() {
      var size = readSize();
      if (value) { value.textContent = size; value.hidden = !size; }
      if (none) none.hidden = !!size;
      if (clear) clear.hidden = !size;
    }

    if (clear) clear.addEventListener('click', function () { clearSize(); });
    document.addEventListener('dv:size:change', paint);
    paint();
  }

  /* --------------------------------------------------------- product cards */
  function variantForSize(variants, size) {
    if (!size) return null;
    var want = String(size).toLowerCase();
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j]).toLowerCase() === want) return variants[i];
      }
    }
    return null;
  }

  function anyOtherAvailable(variants, chosen) {
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] !== chosen && variants[i].available) return true;
    }
    return false;
  }

  /* The strip is deliberately empty and hidden unless it can say something a
     shopper could act on. Liquid prints it for a sold-out product; this
     reveals it for the shopper's OWN saved size when that size resolves
     against this product's real variants, and never otherwise. It invents no
     state, no count and no delivery promise. */
  function paintCard(card) {
    var data = $('[data-dv-card-variants]', card);
    var strip = $('[data-dv-card-stock]', card);
    var media = strip ? strip.parentNode : null;
    var addBtn = $('[data-dv-card-addbtn]', card);
    var addInput = $('[data-dv-card-variant]', card);
    if (!data) return;

    var variants;
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    /* The server already decided this product is sold out. That verdict is
       never overwritten by anything below. */
    var soldOut = !!(strip && strip.classList.contains('dv-pcard__stock--out') && !strip.hidden);

    var size = CFG.sizeAware ? readSize() : '';
    var match = variantForSize(variants, size);

    var start = strip && $('[data-dv-card-stock-start]', strip);
    var end = strip && $('[data-dv-card-stock-end]', strip);

    function showStrip(on) {
      if (strip) strip.hidden = !on;
      if (media) media.classList.toggle('dv-has-strip', on);
    }

    /* No saved size, or this product does not come in it: leave the card
       exactly as the server drew it. */
    if (!match) {
      if (!soldOut) {
        if (start) start.textContent = '';
        if (end) end.textContent = '';
        if (strip) strip.classList.remove('dv-pcard__stock--low');
        showStrip(false);
      } else {
        showStrip(true);
      }
      if (addBtn && variants.length > 1) addBtn.hidden = true;
      return;
    }

    if (match.available) {
      if (strip) strip.classList.remove('dv-pcard__stock--out');
      if (start) start.textContent = t('size_in_stock', { size: size });

      var showCount = CFG.lowStock &&
        typeof match.qty === 'number' &&
        match.qty > 0 &&
        match.qty <= CFG.threshold;
      if (end) end.textContent = showCount ? t('units_left', { count: match.qty }) : '';
      if (strip) strip.classList.toggle('dv-pcard__stock--low', showCount);

      if (addBtn && addInput) {
        addInput.value = match.id;
        addBtn.textContent = t('add_size', { size: size }) || addBtn.getAttribute('data-label-add') || '';
        addBtn.hidden = false;
      }
    } else {
      if (strip) {
        strip.classList.add('dv-pcard__stock--out');
        strip.classList.remove('dv-pcard__stock--low');
      }
      if (start) start.textContent = t('size_sold_out', { size: size });
      if (end) end.textContent = anyOtherAvailable(variants, match) ? t('other_sizes') : '';
      if (addBtn) addBtn.hidden = true;
    }
    showStrip(true);
  }

  function initCards() {
    function paintAll() { $$('[data-dv-product-card]').forEach(paintCard); }
    document.addEventListener('dv:size:change', paintAll);
    /* Rails and recommendations arrive after the first paint. */
    document.addEventListener('dv:section:loaded', paintAll);
    paintAll();
  }

  /* Quick add is a real product form. cart.js already owns every add-to-cart
     in this theme — it posts, re-renders the drawer from Liquid, updates the
     count and shows the just-added confirmation — so this file deliberately
     contains no second implementation. The card form simply carries
     data-dv-product-form and cart.js takes it from there. */

  function init() {
    initPanes();
    initFacets();
    initSortSheet();
    initSizeChip();
    initAccountSize();
    initCards();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
